import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from '../compat/router-shim';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import azureAuthService from '../services/azureAuthService';
import { session } from '../services/auth/session';
import { logger } from '../utils/logger';
import { tracker } from '../services/tracker';
import { routePaths } from '../routes/basePaths';
import useStaticImage from '../hooks/useStaticImage';
import useQueryStringParameters from '../hooks/useQueryStringParameters';
import { useAtomValue } from 'jotai';
import { hasUpdateAtom, updateVersionAtom } from '../store/update/updateState';
import { ClearSWCacheModal } from '../components/molecules/ClearSWCacheModal';
import { Trash2 } from 'lucide-react';
import { usePWAStatus } from '../pwa';
import { v4 } from 'uuid';
import LoadingPage from './LoadingPage';
import { updateService } from '../services/updateService';

export const LoginScreen = () => {
  console.log('🔵 LoginScreen: Component rendering');

  const [username, setUsername] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [timeoutId] = useState<string>(v4());
  const { status: pwaStatus } = usePWAStatus({
    timeoutId: timeoutId,
    timeoutMs: 60000,
  });

  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [isUpdating, setIsUpdating] = useState(false);
  // The app host did not answer. The update stays pending, so this stops the
  // effect below retrying in a loop; it is cleared when the device reconnects.
  const [autoUpdateRefused, setAutoUpdateRefused] = useState(false);

  console.log('🔵 LoginScreen: Auth state', {
    isAuthenticated,
    accountCount: accounts.length,
    inProgress,
    hasToken: !!session.getToken(),
  });

  const hasUpdate = useAtomValue(hasUpdateAtom);
  const updateVersion = useAtomValue(updateVersionAtom);
  const [showClearCacheModal, setShowClearCacheModal] = useState(false);

  const backgroundImage = useStaticImage('images/login_background.png');
  const logoImage = useStaticImage('images/logo.svg');

  const [isLoadingAuth, setIsLoadingAuth] = useState(false);

  const { getQueryParam } = useQueryStringParameters();

  // Check authentication status and redirect if authenticated
  useEffect(() => {
    const logoutParam = getQueryParam({ param: 'logout' });
    if (logoutParam) {
      const timerId = setTimeout(() => {
        void navigate('/login', {
          replace: true,
        });
      }, 1000);
      return () => clearTimeout(timerId);
    }
    if (
      isAuthenticated &&
      accounts.length > 0 &&
      inProgress === InteractionStatus.None &&
      !logoutParam
    ) {
      console.log('User is authenticated, continuing to the app...');
      setIsLoadingAuth(true);

      const handleAuthenticatedUser = async () => {
        try {
          // A stored token is used as it is, even offline: renewing it is the session's job.
          if (!session.getToken()) {
            await session.refresh();
          }

          const signedInUsername = accounts[0].username || '';
          tracker.setUserID(signedInUsername);
          if (rememberMe) {
            azureAuthService.rememberMe.saveUsername(signedInUsername);
          }

          void navigate(routePaths.Home);
        } catch (error) {
          console.error('Error getting token for authenticated user:', error);
          setError(
            'Failed to get authentication token. Please try logging in again.',
          );
        } finally {
          setIsLoadingAuth(false);
        }
      };

      void handleAuthenticatedUser();
    }
  }, [isAuthenticated, accounts, inProgress, navigate, rememberMe]);

  // Load remembered username on component mount
  useEffect(() => {
    const rememberedUser = azureAuthService.rememberMe.getUsername();
    if (rememberedUser) {
      setUsername(rememberedUser);
      setRememberMe(true);
    }
  }, []);

  // Handle Sign in with Microsoft button click
  const handleMicrosoftLogin = async () => {
    setIsLoadingAuth(true);
    if (inProgress !== InteractionStatus.None) {
      console.log('Authentication already in progress, please wait...');
      return;
    }

    setError('');

    try {
      console.log('Starting Microsoft login process with redirect...');

      // Set a flag so the route loader knows we're processing a login
      sessionStorage.setItem('msalLoginInProgress', 'true');

      // Use redirect method
      const loginRequest = {
        scopes: [import.meta.env.VITE_AZURE_SCOPE],
        prompt: 'select_account' as const,
        redirectUri: import.meta.env.VITE_AZURE_REDIRECT_URI,
        loginHint:
          username || azureAuthService.rememberMe.getUsername() || undefined,
      };

      logger.info(
        'Starting login redirect with request: ' +
          JSON.stringify({
            scopes: loginRequest.scopes,
            redirectUri: loginRequest.redirectUri,
            prompt: loginRequest.prompt,
          }),
      );

      // This will navigate away from the app to the Azure AD login page
      await instance.loginRedirect(loginRequest);

      // Code below won't execute as we navigate away
    } catch (error) {
      console.error('Login redirect error:', error);
      setError('Failed to sign in. Please try again.');
      setIsLoadingAuth(false);
    }
  };

  // Show loading state while MSAL is processing
  const isLoading = useMemo(() => {
    return (
      inProgress !== InteractionStatus.None ||
      isLoadingAuth ||
      pwaStatus !== 'ready'
    );
  }, [inProgress, isLoadingAuth, pwaStatus]);

  const updateAvailable = useMemo(() => {
    return hasUpdate && pwaStatus === 'ready';
  }, [hasUpdate, pwaStatus]);

  // Skipped while offline, so run it once the device is back online. The
  // device's own event, as the online status means the API answers (#11623).
  useEffect(() => {
    const retry = () => setAutoUpdateRefused(false);
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, []);

  useEffect(() => {
    if (
      updateAvailable &&
      !isUpdating &&
      !autoUpdateRefused &&
      updateVersion &&
      !updateService.wasAutoUpdateAttempted(updateVersion)
    ) {
      setIsUpdating(true);
      const startUpdate = async () => {
        const result = await updateService.updateToNewVersion();
        if (result === 'unreachable') {
          // Stay on the login screen with the cached app.
          setAutoUpdateRefused(true);
          setIsUpdating(false);
          return;
        }
        // The host answered, so this was a real attempt: don't make it again this
        // session, or a clear that keeps failing would block every launch.
        updateService.markAutoUpdateAttempted(updateVersion);
        if (result === 'failed') {
          setError(
            'The app update did not finish. You can sign in and keep using this version.',
          );
          setIsUpdating(false);
        }
      };
      setTimeout(() => {
        void startUpdate();
      }, 100);
    }
  }, [updateAvailable, isUpdating, autoUpdateRefused, updateVersion]);

  if (isUpdating) {
    return (
      <LoadingPage loadingMessage="Updating application, please wait..." />
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={backgroundImage}
          alt="Login Background"
          className="size-full object-cover"
        />
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md">
        <div className="transform overflow-hidden bg-white shadow-2xl transition-all duration-300 hover:scale-[1.01]">
          {/* Header with Logo */}
          <div className="flex flex-col items-center p-8 pb-0">
            <div className="mb-6 flex h-20 w-full justify-center">
              <img src={logoImage} alt="Bradken Logo" className="h-full" />
            </div>
            <h2 className="mb-2 text-center text-3xl font-bold text-ink">
              Bradken Inspect
            </h2>
            <p className="mb-8 max-w-xs text-center text-inkMuted">
              Not a member?
              <br />
              Email us at{' '}
              <a
                href="mailto:digitalsupport@bradken.com"
                className="text-primaryColor hover:underline"
              >
                digitalsupport@bradken.com
              </a>
            </p>
          </div>

          {/* Form Section */}
          <div className="p-8 pt-4">
            {error && (
              <div className="mb-6 border-l-4 border-red-500 bg-red-50 p-3 text-red-700">
                <div className="flex items-center">
                  <svg
                    className="mr-2 size-5 text-red-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <span>{error}</span>
                </div>
              </div>
            )}

            <div className="space-y-6">
              {/* Microsoft Sign-in Button */}
              <div>
                <button
                  type="button"
                  onClick={handleMicrosoftLogin}
                  disabled={isLoading}
                  className="flex w-full items-center justify-center border border-line bg-white px-4 py-3 text-sm font-normal text-ink transition-colors hover:bg-brandLight/20 focus:ring-2 focus:ring-primaryColor focus:ring-offset-0 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isLoading ? (
                    <svg
                      className="mr-3 -ml-1 size-5 animate-spin text-ink"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <svg
                      className="mr-2 size-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 23 23"
                    >
                      <rect x="1" y="1" width="10" height="10" fill="#f25022" />
                      <rect
                        x="12"
                        y="1"
                        width="10"
                        height="10"
                        fill="#7fba00"
                      />
                      <rect
                        x="1"
                        y="12"
                        width="10"
                        height="10"
                        fill="#00a4ef"
                      />
                      <rect
                        x="12"
                        y="12"
                        width="10"
                        height="10"
                        fill="#ffb900"
                      />
                    </svg>
                  )}
                  {inProgress !== InteractionStatus.None
                    ? 'Signing in...'
                    : 'Sign in with Microsoft'}
                </button>
                {pwaStatus !== 'ready' && (
                  <div className="flex items-center justify-center bg-white px-4 py-3 text-sm font-normal text-ink transition-colors hover:bg-brandLight/20 focus:ring-2 focus:ring-primaryColor focus:ring-offset-0 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50">
                    Loading app for offline use...
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-line bg-gray-50 px-8 py-4 text-center">
            <p className="text-sm text-inkMuted">
              &copy; {new Date().getFullYear()} Bradken. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* Add icon button to bottom right hand corner to clear cache */}
      <div className="fixed right-4 bottom-4">
        <button
          onClick={() => {
            setShowClearCacheModal(true);
          }}
          aria-label="Clear app cache"
          className="rounded-full bg-white p-2"
        >
          <Trash2 className="size-6 text-ink" />
        </button>
      </div>

      {/* Kept outside the login card: the card's hover scale makes it the
          containing block for this fixed overlay, so the overlay shrank to the
          card and flickered as the pointer left and re-entered it. */}
      <ClearSWCacheModal
        isOpen={showClearCacheModal}
        onClose={() => setShowClearCacheModal(false)}
      />
    </div>
  );
};
